import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, LinearProgress, Typography } from '@mui/material';

import NavBaseTemplate from '../templates/NavBaseTemplate';
import DoorCanvassDetail from '../doors/DoorCanvassDetail';
import api from '../../store/api/api';
import {
  pullCampaignOffices,
  pullCampaignScripts,
} from '../../store/api/calls';
import {
  pullCampaignDoorCanvasses,
  pullCampaignDoorCanvassPasses,
} from '../../store/api/calls/doors';
import type { DoorCanvass, DoorCanvassPass } from '@/types/doorcanvass';

interface RootState {
  session: { focusedCampaign: { id: number } };
  doorCanvasses: { data: DoorCanvass[]; loading: boolean };
  doorCanvassPasses: { data: DoorCanvassPass[]; loading: boolean };
  offices: { data: { id: number; name: string }[] };
  scripts: { data: { id: number; name: string }[] };
}

export default function DoorCanvassDetailScreen() {
  const { doorcanvass_id: doorcanvassIdParam } = useParams();
  const doorcanvassId = Number(doorcanvassIdParam) || 0;

  const focusedCampaign = useSelector(
    (state: RootState) => state.session.focusedCampaign
  );
  const doorCanvasses = useSelector((state: RootState) => state.doorCanvasses.data);
  const doorCanvassPasses = useSelector(
    (state: RootState) => state.doorCanvassPasses.data
  );
  const passesLoading = useSelector(
    (state: RootState) => state.doorCanvassPasses.loading
  );
  const canvassesLoading = useSelector(
    (state: RootState) => state.doorCanvasses.loading
  );
  const offices = useSelector((state: RootState) => state.offices.data);
  const scripts = useSelector((state: RootState) => state.scripts.data);

  const [fetchedCanvass, setFetchedCanvass] = useState<DoorCanvass | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadCaches = useCallback(() => {
    pullCampaignOffices();
    pullCampaignScripts();
    pullCampaignDoorCanvasses();
    pullCampaignDoorCanvassPasses();
  }, []);

  useEffect(() => {
    loadCaches();
  }, [focusedCampaign.id, loadCaches]);

  const listCanvass = useMemo(
    () => doorCanvasses.find((d) => d.id === doorcanvassId),
    [doorCanvasses, doorcanvassId]
  );

  useEffect(() => {
    if (!doorcanvassId) {
      setFetchError('Invalid door canvass id');
      return;
    }

    if (listCanvass?.statuses?.length) {
      setFetchedCanvass(null);
      setFetchError(null);
      return;
    }

    setDetailLoading(true);
    setFetchError(null);
    api
      .get(`/doorcanvasses/${doorcanvassId}/`)
      .then((res) => {
        const fromApi = res.data as DoorCanvass;
        const merged: DoorCanvass = {
          ...fromApi,
          statuses: fromApi.statuses?.length
            ? fromApi.statuses
            : listCanvass?.statuses,
        };
        setFetchedCanvass(merged);
      })
      .catch(() => {
        if (!listCanvass) {
          setFetchError('Could not load this door canvass.');
        }
      })
      .finally(() => setDetailLoading(false));
  }, [doorcanvassId, listCanvass]);

  const canvass: DoorCanvass | undefined = useMemo(() => {
    if (fetchedCanvass) return fetchedCanvass;
    if (listCanvass) return listCanvass;
    return undefined;
  }, [fetchedCanvass, listCanvass]);

  const passes = useMemo(
    () =>
      doorCanvassPasses.filter((p) => p.canvass?.id === doorcanvassId),
    [doorCanvassPasses, doorcanvassId]
  );

  const lookups = useMemo(() => {
    if (!canvass) return {};
    const officeName = offices.find((o) => o.id === canvass.office)?.name;
    const scriptName = scripts.find((s) => s.id === canvass.script)?.name;
    return { officeName, scriptName };
  }, [canvass, offices, scripts]);

  const loading =
    detailLoading || passesLoading || canvassesLoading;

  return (
    <NavBaseTemplate>
      {(loading && !canvass) && <LinearProgress sx={{ mb: 2 }} />}

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchError}
        </Alert>
      )}

      {!canvass && !loading && !fetchError && (
        <Typography color="text.secondary">Door canvass not found.</Typography>
      )}

      {canvass && (
        <DoorCanvassDetail
          canvass={canvass}
          passes={passes}
          lookups={lookups}
        />
      )}
    </NavBaseTemplate>
  );
}
